// Flutter-style Brand Manager - exact match

export const ONOFF_BRAND = 'onoff' as const
export const GIMI_BRAND = 'gimi' as const
export const MAKB_BRAND = 'makbous' as const

const RESTAURANT_MAP_URL_TEMPLATE = 'https://besalla.com/{lang}/restaurant-map'

export interface BrandInfo {
  currentBrand: string
  apiUrl: string
  currentAppName: string
  currentBrandName: string
  currentLogoUrl: string
  currentPrimaryColor: string
  currentSecondaryColor: string
  currentAccentColor: string
}

class BrandManager {
  private static instance: BrandManager
  private _currentBrand = ''
  private _apiUrl = ''

  private constructor() {
    this._detectBrand()
  }

  static getInstance(): BrandManager {
    if (!BrandManager.instance) {
      BrandManager.instance = new BrandManager()
    }
    return BrandManager.instance
  }

  private _detectBrand(): void {
    const url = window.location.origin
    
    if (url.includes('https://agent.alanwarmodern.com')) {
      this._currentBrand = 'anwar'
      this._apiUrl = 'https://anwar.besalla.com/api'
    }
    else if (url.includes('localhost:3000')) {
      this._currentBrand = "GIMI_BRAND"
      this._apiUrl = 'https://tandoor.besalla.com/api'
    }
    else if (url.includes('https://tandoor-qr.besalla.com')) {
      this._currentBrand = 'tandoor'
      this._apiUrl = 'https://tandoor.besalla.com/api'
    }
    else if (url.includes('besalla-qr.bdaiat.com/')) {
      this._currentBrand = GIMI_BRAND
      this._apiUrl = 'https://gimi.besalla.com/api'
    }
    else if (url.includes('besalla-qr.bdaiat.com')) {
      this._currentBrand = GIMI_BRAND
      this._apiUrl = 'https://makbous.besalla.com/api'
    }
    else {
      // Default fallback - will be updated from API
      this._currentBrand = GIMI_BRAND
      this._apiUrl = 'https://makbous.besalla.com/api'
    }
  }

  get currentBrand(): string {
    return this._currentBrand
  }

  get apiUrl(): string {
    return this._apiUrl
  }

  getRestaurantMapUrl(language: string): string {
    const envUrl = process.env.REACT_APP_BESALLA_RESTAURANT_MAP_URL
    if (envUrl) {
      return envUrl
    }

    const langCode = language === 'ar' ? 'ar' : 'en'
    return RESTAURANT_MAP_URL_TEMPLATE.replace('{lang}', langCode)
  }

  get isOnOffBrand(): boolean {
    return this._currentBrand === ONOFF_BRAND
  }
  
  get currentAppName(): string {
    switch (this._currentBrand) {
      case 'alanwarmodern':
        return 'Alanwar Modern Cashier'
      case ONOFF_BRAND:
        return 'OnOff Cashier'
      case 'makbous':
      default:
        return 'Makbous Cashier'
    }
  }

  get currentBrandName(): string {
    switch (this._currentBrand) {
      case 'alanwarmodern':
        return 'Alanwar Modern'
      case ONOFF_BRAND:
        return 'OnOff'
      case 'makbous':
      default:
        return 'Makbous'
    }
  }

  get currentLogoUrl(): string {
    switch (this._currentBrand) {
      case 'alanwarmodern':
        return 'https://anwar.besalla.com/storage/logos/alanwar_logo.png'
      case 'makbous':
      default:
        return 'https://onoff.besalla.com/storage/logos/onoff_logo.png'
    }
  }

  get currentPrimaryColor(): string {
    switch (this._currentBrand) {
      case 'alanwarmodern':
        return '#FF6B35'
      case 'makbous':
      default:
        return '#E91E63'
    }
  }

  get currentSecondaryColor(): string {
    switch (this._currentBrand) {
      case 'alanwarmodern':
        return '#F7931E'
      case 'makbous':
      default:
        return '#F06292'
    }
  }

  get currentAccentColor(): string {
    switch (this._currentBrand) {
      case 'alanwarmodern':
        return '#FFD23F'
      case 'makbous':
      default:
        return '#F8BBD9'
    }
  }

  isFeatureEnabled(feature: string): boolean {
    switch (this._currentBrand) {
      case 'alanwarmodern':
        return true // All features enabled
      case 'makbous':
      default:
        return feature !== 'dine_in' // Dine-in disabled for makbous
    }
  }
  
  /// Update brand info from API response - exact match
  updateFromApi(apiData: Record<string, any>): void {
    if (apiData.brand) {
      this._currentBrand = apiData.brand.toString()
    }
    if (apiData.api_url) {
      this._apiUrl = apiData.api_url.toString()
    }
  }

  getBrandInfo(): BrandInfo {
    return {
      currentBrand: this._currentBrand,
      apiUrl: this._apiUrl,
      currentAppName: this.currentAppName,
      currentBrandName: this.currentBrandName,
      currentLogoUrl: this.currentLogoUrl,
      currentPrimaryColor: this.currentPrimaryColor,
      currentSecondaryColor: this.currentSecondaryColor,
      currentAccentColor: this.currentAccentColor,
    }
  }
}

// Export singleton instance
export const brandManager = BrandManager.getInstance()
export default brandManager
